<template>
  <div class="edit-course-page">
    <div class="header">
      <h1>Edit CE Course</h1>
      <p class="subtitle">Update information about this continuing education course</p>
    </div>

    <div v-if="loading" class="loading">
      <p>Loading course information...</p>
    </div>

    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button @click="goBack" class="btn btn-secondary">Go Back</button>
    </div>

    <form v-else @submit.prevent="handleSubmit" class="course-form">
      <!-- Course Name -->
      <div class="form-group">
        <label for="courseName">Course Name <span class="required">*</span></label>
        <input
          type="text"
          id="courseName"
          v-model="formData.name"
          required
          placeholder="Enter the course name"
          class="form-input"
        />
      </div>

      <!-- Status (review workflow) – admin only -->
      <div v-if="userIsAdmin" class="form-group">
        <label for="status">Status</label>
        <select
          id="status"
          v-model="formData.status"
          class="form-input"
        >
          <option v-for="opt in courseStatuses" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
        <small class="form-help">Needs Review (new), Info Missing, Inactive, or Active (visible in search)</small>
      </div>

      <!-- Provider Search -->
      <ProviderSelector
        ref="providerSelector"
        label="Provider"
        :required="true"
        :require-postcode="true"
        input-id="editCourseProviderSearch"
        :initial-value="initialProvider"
        @provider-ready="handleProviderReady"
        @create-new-provider="handleCreateNewProvider"
      />

      <!-- Provider Form (shown when creating new provider) -->
      <div v-if="showProviderForm" class="provider-form-wrapper">
        <ProviderForm
          ref="providerForm"
          :initial-name="newProviderName"
          input-id="editCourseProviderForm"
          :require-postcode="true"
          @form-ready="handleProviderFormReady"
        />
        <div class="provider-form-actions">
          <button 
            type="button" 
            @click="handleCreateProvider" 
            :disabled="isCreatingProvider || !isProviderFormReady"
            class="btn-primary"
          >
            {{ isCreatingProvider ? 'Creating...' : 'Create Provider' }}
          </button>
          <button 
            type="button" 
            @click="handleCancelProviderForm"
            class="btn-secondary"
          >
            Cancel
          </button>
        </div>
      </div>

      <!-- Instructor -->
      <div class="form-group">
        <label for="instructor">Instructor (Optional)</label>
        <input
          type="text"
          id="instructor"
          v-model="formData.instructor"
          placeholder="Name of instructor"
          class="form-input"
        />
      </div>

      <!-- Credits -->
      <div class="form-group">
        <label for="credits">CE Credits <span class="required">*</span></label>
        <input
          type="number"
          id="credits"
          v-model.number="formData.credits"
          required
          min="0"
          step="0.25"
          placeholder="e.g. 2.25"
          class="form-input"
        />
        <small class="form-help">Enter the number of continuing education credits (up to 2 decimal places; courses can be worth quarters, e.g. 0.25, 0.5, 0.75)</small>
      </div>

      <!-- Cost -->
      <div class="form-group">
        <label for="cost">Cost <span class="required">*</span></label>
        <div class="cost-input-group">
          <select
            id="costType"
            v-model="formData.costType"
            required
            class="form-input cost-type"
          >
            <option value="free">Free</option>
            <option value="paid">Paid</option>
          </select>
          <input
            v-if="formData.costType === 'paid'"
            type="number"
            id="cost"
            v-model.number="formData.cost"
            required
            min="0"
            step="0.01"
            placeholder="0.00"
            class="form-input cost-amount"
          />
        </div>
      </div>

      <!-- Course Type (Online/In-person) -->
      <div class="form-group">
        <label for="courseType">Course / Activity Type <span class="required">*</span></label>
        <select
          id="courseType"
          v-model="formData.courseType"
          class="form-input"
          required
        >
          <option value="" disabled>Select activity type</option>
          <option
            v-for="type in courseTypes"
            :key="type"
            :value="type"
          >
            {{ type }}
          </option>
        </select>
      </div>

      <!-- Date/Time -->
      <div class="form-group">
        <label for="dateTime">Date and Time (Optional)</label>
        <input
          type="datetime-local"
          id="dateTime"
          v-model="formData.dateTime"
          class="form-input"
        />
        <small class="form-help">When is this course scheduled?</small>
      </div>

      <!-- Location -->
      <div class="form-group">
        <label for="location">Location (Optional)</label>
        <input
          type="text"
          id="location"
          v-model="formData.location"
          placeholder="City, State or full address"
          class="form-input"
        />
      </div>

      <!-- Website URL -->
      <div class="form-group">
        <label for="website">Website URL (Optional)</label>
        <input
          type="url"
          id="website"
          v-model="formData.website"
          placeholder="https://example.com"
          class="form-input"
        />
      </div>

      <!-- Description -->
      <div class="form-group">
        <label for="description">Description (Optional)</label>
        <textarea
          id="description"
          v-model="formData.description"
          rows="4"
          placeholder="Additional information about the course"
          class="form-input"
        ></textarea>
      </div>

      <!-- Performance Indicators -->
      <div class="form-group">
        <label>Performance Indicators</label>
        <p class="form-help">Tag this course with competencies it addresses (RDN/NDTR).</p>
        <div v-if="loadingPIs" class="pi-loading">Loading indicators...</div>
        <template v-else>
          <div v-if="selectedPIs.length > 0" class="pi-tags">
            <span
              v-for="pi in selectedPIs"
              :key="pi.id"
              class="pi-tag"
            >
              {{ pi.code }}: {{ piLabel(pi) }}
              <button
                type="button"
                class="pi-tag-remove"
                :aria-label="'Remove ' + pi.code"
                @click="removePI(pi.id)"
              >
                ×
              </button>
            </span>
          </div>
          <div v-else class="pi-none">No performance indicators attached.</div>
          <div class="pi-add">
            <select
              id="addPI"
              v-model="addPIDropdown"
              class="form-input pi-select"
              @change="onAddPISelect"
            >
              <option value="">Add a performance indicator...</option>
              <option
                v-for="pi in availablePIs"
                :key="pi.id"
                :value="pi.id"
              >
                {{ pi.code }}: {{ piLabel(pi) }}
              </option>
            </select>
          </div>
        </template>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <button type="button" @click="handleCancel" class="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" :disabled="isSubmitting" class="btn btn-primary">
          {{ isSubmitting ? 'Updating...' : 'Update Course' }}
        </button>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="successMessage" class="message message-success">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="message message-error">
        {{ errorMessage }}
      </div>
    </form>
  </div>
</template>

<script>
import { updateCourse, getCourseById } from '../../services/courseService'
import { getPerformanceIndicators } from '../../services/performanceIndicatorService.js'
import { getCurrentUser } from '../../services/authService.js'
import { getCourseTypes, COURSE_STATUSES } from '../../models/Course.js'
import ProviderSelector from '../shared/ProviderSelector.vue'
import ProviderForm from '../shared/ProviderForm.vue'
import { createProvider } from '../../services/providerService.js'

export default {
  name: 'EditCourse',
  components: {
    ProviderSelector,
    ProviderForm
  },
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      formData: {
        name: '',
        provider_id: null,
        status: 'needs_review',
        instructor: '',
        hours: null,
        credits: null,
        costType: 'free',
        cost: null,
        courseType: '',
        dateTime: '',
        location: '',
        website: '',
        description: ''
      },
      selectedProvider: null,
      initialProvider: null,
      isSubmitting: false,
      successMessage: '',
      errorMessage: '',
      showProviderForm: false,
      newProviderName: '',
      isCreatingProvider: false,
      isProviderFormReady: false,
      userIsAdmin: false,
      allPerformanceIndicators: [],
      selectedPerformanceIndicatorIds: [],
      loadingPIs: false,
      addPIDropdown: ''
    }
  },
  computed: {
    selectedPIs() {
      if (!this.allPerformanceIndicators.length) return []
      const ids = this.selectedPerformanceIndicatorIds.map(i => Number(i))
      return this.allPerformanceIndicators.filter(pi => ids.includes(Number(pi.id)))
    },
    availablePIs() {
      const ids = this.selectedPerformanceIndicatorIds.map(i => Number(i))
      return this.allPerformanceIndicators.filter(pi => !ids.includes(Number(pi.id)))
    },
    courseTypes() {
      return getCourseTypes()
    },
    courseStatuses() {
      return COURSE_STATUSES
    }
  },
  async mounted() {
    const userPromise = getCurrentUser()
    await Promise.all([this.loadCourse(), this.loadPerformanceIndicators()])
    const user = await userPromise
    this.userIsAdmin = user?.isAdmin === true
  },
  methods: {
    async loadCourse() {
      this.loading = true
      this.error = null

      try {
        const course = await getCourseById(this.id)
        
        if (!course) {
          this.error = 'Course not found.'
          this.loading = false
          return
        }

        // Populate form with course data
        this.formData = {
          name: course.name || '',
          provider_id: course.providerId || null,
          status: course.status || 'needs_review',
          instructor: course.instructor || '',
          hours: course.hours || null,
          credits: course.credits || null,
          costType: course.costType || (course.cost === 0 || course.cost === null ? 'free' : 'paid'),
          cost: course.cost || null,
          courseType: course.courseType || '',
          dateTime: course.dateTime ? this.formatDateTimeForInput(course.dateTime) : '',
          location: course.location || '',
          website: course.website || '',
          description: course.description || ''
        }

        // Set initial provider for ProviderSelector
        if (course.provider) {
          this.initialProvider = course.provider
          this.selectedProvider = course.provider
        }

        // Set performance indicators (IDs) for PI section
        if (course.performanceIndicators && Array.isArray(course.performanceIndicators)) {
          this.selectedPerformanceIndicatorIds = course.performanceIndicators.map(pi => pi.id)
        } else {
          this.selectedPerformanceIndicatorIds = []
        }

        this.loading = false
      } catch (err) {
        this.error = 'Failed to load course information.'
        console.error('Error loading course:', err)
        this.loading = false
      }
    },
    formatDateTimeForInput(dateTimeString) {
      if (!dateTimeString) return ''
      // Convert ISO string to datetime-local format (YYYY-MM-DDTHH:mm)
      const date = new Date(dateTimeString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },
    handleProviderReady(provider) {
      this.selectedProvider = provider
      this.formData.provider_id = provider.id
      this.showProviderForm = false
      this.errorMessage = ''
    },
    handleCreateNewProvider(searchTerm) {
      this.showProviderForm = true
      this.newProviderName = searchTerm || ''
      this.selectedProvider = null
      this.formData.provider_id = null
    },
    handleProviderFormReady(isReady) {
      this.isProviderFormReady = isReady
    },
    async handleCreateProvider() {
      if (!this.$refs.providerForm) {
        this.errorMessage = 'Form not available.'
        return
      }

      const formData = this.$refs.providerForm.getFormData()

      // Validate required fields
      if (!formData.name || !formData.provider_type || 
          !formData.website || !formData.description) {
        this.errorMessage = 'Please fill in all required provider fields.'
        return
      }

      if (!formData.postcode) {
        this.errorMessage = 'Postcode is required.'
        return
      }

      this.isCreatingProvider = true
      this.errorMessage = ''

      try {
        const newProvider = await createProvider(formData)
        this.selectedProvider = newProvider
        this.formData.provider_id = newProvider.id
        this.showProviderForm = false
        this.newProviderName = ''
        
        // Update ProviderSelector to reflect the newly created provider
        if (this.$refs.providerSelector) {
          this.$refs.providerSelector.selectedProvider = newProvider
          this.$refs.providerSelector.providerSearchTerm = newProvider.name
          this.$refs.providerSelector.matchingProviders = []
        }
      } catch (error) {
        this.errorMessage = error.message || 'Failed to create provider. Please try again.'
        console.error('Error creating provider:', error)
      } finally {
        this.isCreatingProvider = false
      }
    },
    handleCancelProviderForm() {
      this.showProviderForm = false
      this.newProviderName = ''
      this.isProviderFormReady = false
      if (this.$refs.providerForm) {
        this.$refs.providerForm.reset()
      }
    },
    async loadPerformanceIndicators() {
      this.loadingPIs = true
      try {
        this.allPerformanceIndicators = await getPerformanceIndicators('all')
      } catch (err) {
        console.error('Error loading performance indicators:', err)
        this.allPerformanceIndicators = []
      } finally {
        this.loadingPIs = false
      }
    },
    piLabel(pi) {
      const desc = (pi.description || '').trim()
      return desc.length > 60 ? desc.slice(0, 60) + '…' : desc || pi.code
    },
    removePI(id) {
      this.selectedPerformanceIndicatorIds = this.selectedPerformanceIndicatorIds.filter(i => i !== id)
    },
    onAddPISelect() {
      const id = this.addPIDropdown
      this.addPIDropdown = ''
      if (!id) return
      const numId = Number(id)
      if (!this.selectedPerformanceIndicatorIds.includes(numId)) {
        this.selectedPerformanceIndicatorIds = [...this.selectedPerformanceIndicatorIds, numId]
      }
    },
    async handleSubmit() {
      this.isSubmitting = true
      this.successMessage = ''
      this.errorMessage = ''

      // Get provider from selector (either selected or create new one)
      let providerId = null
      try {
        if (!this.$refs.providerSelector) {
          this.errorMessage = 'Please search for and select a provider, or create a new one.'
          this.isSubmitting = false
          return
        }

        const provider = await this.$refs.providerSelector.getProvider()
        if (!provider || !provider.id) {
          this.errorMessage = 'Please search for and select a provider, or create a new one.'
          this.isSubmitting = false
          return
        }
        providerId = provider.id
      } catch (error) {
        this.errorMessage = error.message || 'Failed to process provider. Please try again.'
        console.error('Error processing provider:', error)
        this.isSubmitting = false
        return
      }

      try {
        // Prepare data for updating
        const courseData = {
          name: this.formData.name.trim(),
          provider_id: providerId,
          status: this.formData.status || 'needs_review',
          hours: this.formData.hours,
          credits: this.formData.credits,
          costType: this.formData.costType,
          courseType: this.formData.courseType
        }

        // Add optional fields if they have values
        if (this.formData.instructor && this.formData.instructor.trim()) {
          courseData.instructor = this.formData.instructor.trim()
        }
        if (this.formData.costType === 'paid' && this.formData.cost !== null) {
          courseData.cost = this.formData.cost
        }
        if (this.formData.dateTime) {
          courseData.dateTime = this.formData.dateTime
        }
        if (this.formData.location && this.formData.location.trim()) {
          courseData.location = this.formData.location.trim()
        }
        if (this.formData.website && this.formData.website.trim()) {
          courseData.website = this.formData.website.trim()
        }
        if (this.formData.description && this.formData.description.trim()) {
          courseData.description = this.formData.description.trim()
        }

        courseData.performanceIndicatorIds = this.selectedPerformanceIndicatorIds

        await updateCourse(this.id, courseData)
        
        this.successMessage = 'Course updated successfully! Redirecting...'
        
        // Navigate to view course page after a short delay
        setTimeout(() => {
          this.$router.push({ name: 'view-course', params: { id: this.id } })
        }, 1500)
      } catch (error) {
        this.errorMessage = 'Failed to update course. Please try again.'
        console.error('Error updating course:', error)
      } finally {
        this.isSubmitting = false
      }
    },
    handleCancel() {
      if (confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
        this.goBack()
      }
    },
    goBack() {
      this.$router.push({ name: 'view-course', params: { id: this.id } })
    }
  }
}
</script>

<style scoped>
.edit-course-page {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
}

.header {
  text-align: center;
  margin-bottom: 40px;
}

.header h1 {
  font-size: 2rem;
  color: var(--color-text-primary);
  margin-bottom: 10px;
}

.subtitle {
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.loading,
.error {
  text-align: center;
  padding: 40px;
  background: var(--color-surface-light);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error {
  color: var(--color-error-main);
}

.course-form {
  background: var(--color-surface-light);
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 8px;
  font-size: 1rem;
}

.required {
  color: var(--color-error-main);
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.form-input:disabled {
  background-color: var(--color-surface-dark);
  cursor: not-allowed;
}

.form-help {
  display: block;
  margin-top: 5px;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
}

.cost-input-group {
  display: flex;
  gap: 10px;
}

.cost-type {
  flex: 0 0 120px;
}

.cost-amount {
  flex: 1;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: normal;
}

.radio-label input[type="radio"] {
  margin-right: 10px;
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.radio-label span {
  color: var(--color-text-primary);
}

textarea.form-input {
  resize: vertical;
  min-height: 100px;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 30px;
  border-top: 2px solid var(--color-border-light);
}

.btn {
  padding: 12px 30px;
  font-size: 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all var(--transition-normal);
}

.btn-primary {
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
}

.btn-primary:disabled {
  background-color: var(--color-border-main);
  cursor: not-allowed;
}

.btn-secondary {
  background-color: var(--color-surface-dark);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border-main);
}

.btn-secondary:hover {
  background-color: var(--color-border-light);
}

.message {
  margin-top: 20px;
  padding: 15px;
  border-radius: 6px;
  font-weight: 500;
}

.message-success {
  background-color: var(--color-success-light);
  color: var(--color-success-dark);
  border: 1px solid var(--color-success-main);
}

.message-error {
  background-color: var(--color-error-light);
  color: var(--color-error-dark);
  border: 1px solid var(--color-error-main);
}

/* Performance indicators */
.pi-loading {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.pi-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.pi-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background-color: var(--color-primary-light, #e8eaf6);
  color: var(--color-text-primary);
  border-radius: 6px;
  font-size: 0.9rem;
}

.pi-tag-remove {
  padding: 0;
  margin: 0;
  margin-left: 2px;
  background: none;
  border: none;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  color: var(--color-text-secondary);
  border-radius: 2px;
}

.pi-tag-remove:hover {
  color: var(--color-error-main);
}

.pi-none {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.pi-add {
  margin-top: 8px;
}

.pi-select {
  max-width: 100%;
}

.provider-form-wrapper {
  margin-top: 20px;
}

.provider-form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border-light);
}

@media (max-width: 768px) {
  .course-form {
    padding: 20px;
  }

  .cost-input-group {
    flex-direction: column;
  }

  .cost-type {
    flex: 1;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }

  .provider-form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>
